import { Fragment, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import {
  IconButton,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import FormLabel from "@mui/material/FormLabel";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import ListItem from "@mui/material/ListItem";
import Collapse from "@mui/material/Collapse";

import CustomChip from "../../../components/mui/chip";
import CustomAvatar from "../../../components/mui/avatar";
import Icon from "../../../components/icon";
import CircularProgress from "@mui/material/CircularProgress";
// import Timeline from '@mui/lab/Timeline'
import MuiTimeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import { format } from "date-fns";
import { DownloadAttachments } from "components/logic/DownloadAttachments";
import AttachmentsSectionEmployee from "screens/Employees/component/AttachmentsSectionEmployee";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { dev_baseUrl } from "utils/var";

const Timeline = styled(MuiTimeline)({
  "& .MuiTimelineItem-root": {
    width: "100%",
    "&:before": {
      display: "none",
    },
  },
});

const QuickView = ({
  handleQuickViewClose,
  jobsite,
  contacts,
  attachments,
  propertyAreaList,
  alertList,
  setAlertList,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openQuickViewContact, setOpenQuickViewContact] = useState(false);
  const [openQuickViewPropArea, setOpenQuickViewPropArea] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState({});

  const [jobsiteAttachments, setJobsiteAttachments] = useState([]);
  const [isJobsiteAttachmentsLoading, setIsJobsiteAttachmentsLoading] =
    useState(false);
  const [groupedJobsiteAttachments, setGroupedJobsiteAttachments] = useState(
    {}
  );

  const [jobsiteThumbs, setJobsiteThumbs] = useState({});

  const getFileType = (filename) => {
    const ext = (filename?.split(".").pop() || "").toLowerCase();
    if (["mp4", "mov", "avi", "webm", "mkv"].includes(ext)) return "video";
    if (
      [
        "txt",
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "csv",
        "json",
        "xml",
      ].includes(ext)
    )
      return "text";
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext))
      return "image";
    return "image";
  };

  // swap these placeholders with your own if you have them already:
  const VideoPlaceholderIcon = "/images/video_placeholder.png";
  const CSV_PlaceholderIcon = "/images/csv.png";
  const PDF_PlaceholderIcon = "/images/pdf.png";
  const DOCX_PlaceholderIcon = "/images/doc.png";
  const XLSX_PlaceholderIcon = "/images/xlsx.png";
  const FilePlaceholder = "/images/placeholder.png";
  const placeholderFor = (name) => {
    const t = getFileType(name);
    if (t === "image") return FilePlaceholder;
    if (t === "text") {
      const e = (name.split(".").pop() || "").toLowerCase();
      if (e === "pdf") return PDF_PlaceholderIcon;
      if (e === "csv") return CSV_PlaceholderIcon;
      if (e === "xls" || e === "xlsx") return XLSX_PlaceholderIcon;
    }
    return FilePlaceholder;
  };

  useEffect(() => {
    let revoked = [];
    const makeThumbs = async () => {
      const map = {};
      for (const att of jobsiteAttachments) {
        const type = getFileType(att.fileName);
        if (type !== "image") {
          map[att.mediaID] = placeholderFor(att.fileName);
          continue;
        }
        try {
          // ⬇️ IMPORTANT: use Jobsite entity here
          const blob = await DownloadAttachments(
            "Attachment",
            "Jobsite",
            jobsite.jobsiteID,
            att.fileName,
            navigate
          );
          if (!(blob instanceof Blob) || blob.size === 0) {
            map[att.mediaID] = FilePlaceholder;
            continue;
          }
          const url = URL.createObjectURL(blob);
          revoked.push(url);
          map[att.mediaID] = url;
        } catch (e) {
          console.error("thumb error", e);
          map[att.mediaID] = FilePlaceholder;
        }
      }
      setJobsiteThumbs(map);
    };
    if (jobsiteAttachments.length) makeThumbs();

    // cleanup object URLs when QuickView unmounts / list changes
    return () => {
      revoked.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [jobsiteAttachments, jobsite?.jobsiteID]);

  // ✅ robust thumbnail fetch
  const getAttachmentThumbnail = async (jobsiteID, filename) => {
    const type = getFileType(filename);

    // non-images → deterministic placeholders
    if (type === "video") return VideoPlaceholderIcon;
    if (type === "text") {
      const ext = filename.split(".").pop()?.toLowerCase();
      if (ext === "csv") return CSV_PlaceholderIcon;
      if (ext === "pdf") return PDF_PlaceholderIcon;
      if (ext === "doc" || ext === "docx") return DOCX_PlaceholderIcon;
      if (ext === "xls" || ext === "xlsx") return XLSX_PlaceholderIcon;
      return FilePlaceholder;
    }

    if (type !== "image") return FilePlaceholder;

    try {
      // ⬇️ pass navigate if your helper redirects on 401
      const blob = await DownloadAttachments(
        "Attachment",
        "Jobsite",
        jobsiteID,
        filename,
        navigate
      );

      // Some backends return application/octet-stream for images.
      // We still can display it; just create an object URL.
      if (!(blob instanceof Blob) || blob.size === 0) return FilePlaceholder;

      // optional: only allow if it *looks* like an image
      const looksLikeImage = (blob.type || "").startsWith("image/");
      const url = URL.createObjectURL(blob);
      return looksLikeImage ? url : url; // still works if octet-stream
    } catch (e) {
      console.error("thumbnail fetch failed", e);
      return FilePlaceholder;
    }
  };

  const downloadAttachment = async (jobsiteID, fileName) => {
    try {
      const blob = await DownloadAttachments(
        "Attachment",
        "Jobsite",
        jobsiteID,
        fileName
      );
      if (!blob || !blob.size) return;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
    } catch (e) {
      console.error("download failed", e);
    }
  };

  useEffect(() => {
    const fetchJobsiteAttachments = async () => {
      if (!jobsite?.jobsiteID) return;
      try {
        const token = localStorage.getItem("jwtToken");
        const res = await fetch(
          `${dev_baseUrl}/Media/getmedia/Attachment/Jobsite/${jobsite.jobsiteID}/0`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (res.ok && !data.isError) setJobsiteAttachments(data.result || []);
      } catch (e) {
        console.error("jobsite attachments fetch failed", e);
      }
    };
    fetchJobsiteAttachments();
  }, [jobsite?.jobsiteID]);

  // group by day (e.g., "September 02, 2025")
  useEffect(() => {
    const grouped = jobsiteAttachments.reduce((acc, a) => {
      const d = new Date(a.createDate);
      if (!isNaN(d)) {
        const key = format(d, "MMMM dd, yyyy");
        (acc[key] ||= []).push(a);
      }
      return acc;
    }, {});
    setGroupedJobsiteAttachments(grouped);
  }, [jobsiteAttachments]);

  // build thumbnails for each grouped attachment
  // useEffect(() => {
  //   const run = async () => {
  //     if (!jobsite?.jobsiteID) return
  //     const map = {}
  //     for (const date in groupedJobsiteAttachments) {
  //       for (const a of groupedJobsiteAttachments[date]) {
  //         map[a.fileName] = await getAttachmentThumbnail(jobsite.jobsiteID, a.fileName)
  //       }
  //     }
  //     setJobsiteThumbnails(map)
  //   }
  //   run()
  // }, [groupedJobsiteAttachments, jobsite?.jobsiteID])

  const filterContacts = () => {
    let updatedCheckedContacts = {};

    const jobsiteContactIDs = (
      jobsite?.contact && Array.isArray(jobsite?.contact)
        ? jobsite?.contact
        : []
    ).map((contact) => parseInt(contact.contactID));

    updatedCheckedContacts = contacts.filter((x) =>
      jobsiteContactIDs.includes(parseInt(x.id))
    );
    setFilteredContacts(updatedCheckedContacts);
  };
  const handleQuickViewContact = (index, value) => {
    setOpenQuickViewContact((prev) => ({
      ...prev,
      [index]: !value,
    }));
  };

  const handleQuickViewPropertyAreaOpen = (index, value) => {
    setOpenQuickViewPropArea((prev) => ({
      ...prev,
      [index]: !value,
    }));
  };
  useEffect(() => {
    filterContacts();
  }, [jobsite, contacts]);

  return (
    <Card
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        width: { xs: "100%", md: "70%" },
        height: "100%",
        zIndex: 10,
        padding: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardHeader
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CustomAvatar
              // sx={{ height: 30, width: 30, fontSize: "12px" }}
              color="success"
              skin="light"
            >
              <Icon icon="tabler:home" />
            </CustomAvatar>
            <Typography variant="h4">{jobsite?.jobsiteName}</Typography>
          </Box>
        }
        action={
          <Tooltip title="Close">
            <IconButton onClick={handleQuickViewClose}>
              <Icon icon="tabler:x" />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent sx={{ overflowY: "auto" }}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <FormLabel>Jobsite Name</FormLabel>
          </Grid>
          <Grid item xs={9}>
            <Typography variant="body1">{jobsite?.jobsiteName}</Typography>
          </Grid>
          <Grid item xs={3}>
            <FormLabel>Jobsite Type</FormLabel>
          </Grid>
          <Grid item xs={9}>
            <Typography variant="body1">{jobsite?.jobsiteType}</Typography>
          </Grid>
          <Grid item xs={3}>
            <FormLabel>Jobsite Address</FormLabel>
          </Grid>
          <Grid item xs={9}>
            <Typography variant="body1">{jobsite?.street1}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider
              sx={{
                color: "text.disabled",
                "& .MuiDivider-wrapper": { px: 6 },
                fontSize: theme.typography.body2.fontSize,
                my: (theme) => `${theme.spacing(3)} !important`,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="bold">
              Contacts ({jobsite?.contact?.length || 0})
            </Typography>
            <List component="nav" aria-label="main mailbox">
              {filteredContacts && filteredContacts.length > 0 ? (
                filteredContacts.map((contact, index) => (
                  <>
                    <ListItem
                      disablePadding
                      key={index}
                      sx={{ borderBottom: "1px solid #E1E4EA" }}
                    >
                      <ListItemButton
                        onClick={() =>
                          handleQuickViewContact(
                            index,
                            openQuickViewContact[index]
                          )
                        }
                      >
                        <Icon
                          icon={
                            openQuickViewContact[index]
                              ? "tabler:chevron-down"
                              : "tabler:chevron-right"
                          }
                        />
                        <ListItemText
                          sx={{ ml: 4 }}
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <CustomAvatar
                                sx={{ height: 25, width: 25, fontSize: "12px" }}
                                color="success"
                              >
                                {contact?.initials}
                              </CustomAvatar>
                              <Typography variant="body1" fontWeight="bold">
                                {contact?.contact}
                              </Typography>
                              <Typography variant="caption">
                                ({contact?.jobTitle})
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    <Collapse
                      in={openQuickViewContact[index]}
                      timeout="auto"
                      unmountOnExit
                    >
                      {/* <Typography>Email : {contact?.email}</Typography>
                                            <Typography>Phone no : {contact?.phoneNumbers.filter(x => x.isPrimary === true)[0].phoneNo}</Typography> */}
                      {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, backgroundColor: '#F6F7FA', p: 6 }}>
                                                <Box sx={{ display: 'flex', gap: 10 }}>
                                                    <FormLabel>Email:</FormLabel>
                                                    <Typography variant='body1'>{contact?.email}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 9 }}>
                                                    <FormLabel>Phone:</FormLabel>
                                                    <Typography variant='body1'>{contact?.phoneNumbers.filter(x => x.isPrimary === true)[0].phoneNo}</Typography>
                                                </Box>
                                            </Box> */}
                                            <Box sx={{ display: 'flex', gap: 20, backgroundColor: '#F6F7FA', p: 6, paddingLeft: 10 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <FormLabel>Full Name</FormLabel>
                                                    <FormLabel>Job Title</FormLabel>
                                                    <FormLabel>Email Address</FormLabel>
                                                    <FormLabel>Primary Number</FormLabel>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Typography variant='body1'>{contact?.contact}</Typography>
                                                    <Typography variant='body1'>{contact?.jobTitle}</Typography>
                                                    <Typography variant='body1'>{contact?.email}</Typography>
                                                    <Typography variant='body1'>{contact?.phoneNumbers.filter(x => x.isPrimary === true)[0].phoneNo}</Typography>
                                                </Box>
                                            </Box>
                                        </Collapse>
                                    </>
                                ))) : (
                                    <Typography>No contacts available.</Typography>
                                    )
                            }                            
                            </List>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='h6' fontWeight='bold'>Property Areas ({propertyAreaList?.length || 0})</Typography>
                            <List component='nav' aria-label='main mailbox'>
                                {propertyAreaList && propertyAreaList.length > 0 ? 
                                (propertyAreaList.map((property, index) => (
                                    <>
                                        <ListItem key={index} disablePadding sx={{ borderBottom: '1px solid #E1E4EA' }}>
                                            <ListItemButton onClick={() => handleQuickViewPropertyAreaOpen(index,openQuickViewPropArea[index])}>
                                                <Icon icon={openQuickViewPropArea[index] ? 'tabler:chevron-down' : 'tabler:chevron-right'} />
                                                <ListItemText sx={{ ml: 4 }} primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Typography variant='body1' fontWeight='bold'>{property.propertyAreaName}</Typography>
                                                        <CustomChip
                                                            skin='light'
                                                            label={property.propertyAreaType}
                                                            color='secondary'
                                                            size='small'
                                                        />
                                                    </Box>
                                                } />
                                            </ListItemButton>
                                        </ListItem>
                                        <Collapse in={openQuickViewPropArea[index]} timeout='auto' unmountOnExit>
                                            {/* <Typography>Description : {property.description === null ? 'no description' : property.description}</Typography> */}
                                            <Box sx={{ display: 'flex', gap: 14, backgroundColor: '#F6F7FA', p: 6, paddingLeft: 10 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <FormLabel>Property Area Name</FormLabel>
                                                    <FormLabel>Property Area Type</FormLabel>
                                                    <FormLabel>Description</FormLabel>
                                                    <FormLabel>Area Measurements</FormLabel>
                                                    {/* <FormLabel>Primary Number</FormLabel> */}
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Typography variant='body1'>{property.propertyAreaName === null ? 'N/A' : property.propertyAreaName}</Typography>
                                                    <Typography variant='body1'>{property.propertyAreaType === null ? 'N/A' : property.propertyAreaType}</Typography>
                                                    <Typography variant='body1' color={property.description === null ? 'text.secondary' : 'text.primary'}>{property.description === null ? 'No description provided' : property.description}</Typography>
                                                    {property?.measurement && property?.measurement.map((measure, index)=>(
                                                        <Box
                                                        key={index}
                                                        sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        borderBottom: "1px solid #E1E4EA",
                                                        pb: 2,
                                                        gap: 30
                                                        }}
                                                        >
                                                        <Box
                                                            sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 2,
                                                            }}
                                                        >
                                                            <Icon icon="tabler:ruler-measure-2" />
                                                            <Typography variant="body1">
                                                            {measure?.measurementName}
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 2,
                                                            }}
                                                        >
                                                            <Typography variant="subtitle2">
                                                            Total size
                                                            </Typography>
                                                            <CustomChip
                                                            skin="light"
                                                            color="primary"
                                                            label={`${measure.total} ${measure.unitOfMeasure}`}
                                                            sx={{ fontWeight: "550" }}
                                                            />
                                                        </Box>
                                                        </Box>
                                                    ))}  
                                                </Box>
                                            </Box>
                                        </Collapse>
                                    </>
                            ))):(
                                <Typography>No Property Area available.</Typography>
                            )}

                        </List>
                    </Grid>
                    <Grid item xs={12}>

                    <Typography variant='h6' fontWeight='bold'>
                      Attachments 
                    </Typography>
                  </Grid>

          <AttachmentsSectionEmployee
            entity="Jobsite"
            entityID={jobsite.jobsiteID}
            userName={`Jobsite`}
            // onDiscardUploads={() => handleOpenUnsavedChangesDlg('upload')}
            employee={[]}
            toast={toast}
          />

          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="bold">
              Alerts ({alertList.length})
            </Typography>
          </Grid>
          {alertList.length > 0 && (
            <>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Icon
                    icon="tabler:circle-filled"
                    fontSize="11px"
                    color={theme.palette.success.main}
                  />
                  <Typography variant="caption" color="text.secondary">
                    ACTIVE ALERTS ({alertList.filter((x) => x.isActive).length})
                  </Typography>
                </Box>
              </Grid>
              {alertList.filter((x) => x.isActive) &&
              alertList.filter((x) => x.isActive).length > 0 ? (
                alertList
                  .filter((x) => x.isActive)
                  .map((Alert, index) => (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          justifyContent: "space-between",
                          backgroundColor: "#F5F7FA",
                          p: 4,
                          borderRadius: 1,
                          borderColor: 'rgba(208, 212, 241, 0.2)'
                        }}
                      >
                        <Typography variant="subtitle2">
                          {Alert.alertDescription}
                        </Typography>
                      </Box>
                    </Grid>
                  ))
              ) : (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "space-between",
                      backgroundColor: theme.palette.mode === "dark" ? "#1B2431" : "#F5F7FA",
                      p: 4,
                      borderRadius: 1,
                      borderColor: 'rgba(208, 212, 241, 0.2)'
                    }}
                  >
                    <Typography variant="subtitle2">
                      No active alerts found.
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Icon
                    icon="tabler:circle-filled"
                    fontSize="11px"
                    color={theme.palette.secondary.main}
                  />
                  <Typography variant="caption" color="text.secondary">
                    INACTIVE ALERTS (
                    {alertList.filter((x) => !x.isActive).length})
                  </Typography>
                </Box>
              </Grid>
              {alertList.filter((x) => !x.isActive) &&
              alertList.filter((x) => !x.isActive).length > 0 ? (
                alertList
                  .filter((x) => !x.isActive)
                  .map((Alert, index) => (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          justifyContent: "space-between",
                          backgroundColor: "#F5F7FA",
                          p: 4,
                          borderRadius: 1,
                          borderColor: 'rgba(208, 212, 241, 0.2)'
                        }}
                      >
                        <Typography variant="subtitle2">
                          {Alert.alertDescription}
                        </Typography>
                      </Box>
                    </Grid>
                  ))
              ) : (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "space-between",
                      backgroundColor: theme.palette.mode === "dark" ? "#1B2431" : "#F5F7FA",
                      p: 4,
                      borderRadius: 1,
                      borderColor: 'rgba(208, 212, 241, 0.2)'
                    }}
                  >
                    <Typography variant="subtitle2">
                      No inactive alerts found.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </>
          )}
          {alertList.length === 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: "space-between",
                  backgroundColor: theme.palette.mode === "dark" ? "#1B2431" : "#F5F7FA",
                  p: 4,
                  borderRadius: 1,
                  borderColor: 'rgba(208, 212, 241, 0.2)'
                }}
              >
                <Typography variant="subtitle2">No alerts found.</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default QuickView;
